import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './LoginModal.css';
import { API_ENDPOINTS } from '../config/api';

const ForgotPasswordModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(1); // 1: Email, 2: Code, 3: New Password
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const resetState = () => {
    setStep(1);
    setEmail("");
    setCode("");
    setNewPassword("");
    setConfirmPassword("");
    setError("");
    setSuccessMessage("");
    setIsLoading(false);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    if (!email) { setError(t('login.validEmail')); return; }

    setIsLoading(true);
    setError("");

    try {
      const response = await fetch(API_ENDPOINTS.REQUEST_PASSWORD_RESET_CODE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email })
      });
      const data = await response.json();

      if (data.success) {
        setStep(2);
        setSuccessMessage(t('login.codeSent'));
      } else {
        setError(data.error || t('login.codeSendFailed'));
      }
    } catch (err) {
      setError(t('login.serverErrorOccurred'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (!code || code.length !== 6) { setError(t('login.enterValidCode')); return; }

    setIsLoading(true);
    setError("");

    try {
      const response = await fetch(API_ENDPOINTS.VERIFY_PASSWORD_CODE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, code })
      });
      const data = await response.json();

      if (data.success) {
        setStep(3);
        setSuccessMessage(t('login.codeVerified'));
      } else {
        setError(data.error || t('login.codeVerifyFailed'));
      }
    } catch (err) {
      setError(t('login.serverErrorOccurred'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { setError(t('login.passwordMinLength')); return; }
    if (newPassword !== confirmPassword) { setError(t('login.passwordMismatch')); return; }

    setIsLoading(true);
    setError("");

    try {
      const response = await fetch(API_ENDPOINTS.RESET_PASSWORD_WITH_CODE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, code, new_password: newPassword })
      });
      const data = await response.json();

      if (data.success) {
        setSuccessMessage(t('login.passwordUpdated'));
        setTimeout(() => {
          handleClose();
        }, 3000);
      } else {
        setError(data.error || t('login.passwordUpdateFailed'));
      }
    } catch (err) {
      setError(t('login.serverErrorOccurred'));
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="login-modal-overlay" onClick={e => e.target === e.currentTarget && handleClose()}>
      <div className="login-modal">
        <div className="login-modal-header">
          <h2>
            {step === 1 && t('login.forgotTitle1')}
            {step === 2 && t('login.forgotTitle2')}
            {step === 3 && t('login.forgotTitle3')}
          </h2>
          <button className="close-button" onClick={handleClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <div className="login-modal-body">
          {successMessage && !error && <div className="success-message">{successMessage}</div>}
          {error && <div className="error-message">{error}</div>}

          {step === 1 && (
            <form onSubmit={handleSendCode} className="login-form">
              <div className="form-group">
                <label htmlFor="forgot-email">{t('login.emailAddress')}</label>
                <div className="input-wrapper">
                  <input
                    type="email"
                    id="forgot-email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder={t('login.emailPlaceholder')}
                    required
                    autoFocus
                  />
                </div>
              </div>
              <button type="submit" className="login-submit-button" disabled={isLoading}>
                {isLoading ? t('login.sending') : t('login.sendCode')}
              </button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={handleVerifyCode} className="login-form">
              <div className="form-group">
                <label htmlFor="code">{t('login.verificationCode')}</label>
                <div className="input-wrapper">
                  <input
                    type="text"
                    id="code"
                    value={code}
                    onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder={t('login.enterCode')}
                    required
                    autoFocus
                    maxLength={6}
                    style={{ letterSpacing: '5px', fontSize: '18px', textAlign: 'center' }}
                  />
                </div>
                <div style={{ marginTop: '10px', fontSize: '13px', color: '#666', textAlign: 'center' }}>
                  {t('login.timeRemaining')}
                </div>
              </div>
              <button type="submit" className="login-submit-button" disabled={isLoading}>
                {isLoading ? t('login.verifying') : t('login.verify')}
              </button>
              <button
                type="button"
                className="forgot-password"
                onClick={() => setStep(1)}
                style={{ marginTop: '15px', width: '100%', textAlign: 'center' }}
              >
                {t('login.goBack')}
              </button>
            </form>
          )}

          {step === 3 && (
            <form onSubmit={handleResetPassword} className="login-form">
              <div className="form-group">
                <label htmlFor="new-password">{t('login.newPassword')}</label>
                <div className="input-wrapper">
                  <input
                    type="password"
                    id="new-password"
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    placeholder={t('login.newPasswordPlaceholder')}
                    required
                    autoFocus
                  />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="confirm-password">{t('login.newPasswordRepeat')}</label>
                <div className="input-wrapper">
                  <input
                    type="password"
                    id="confirm-password"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    placeholder={t('login.newPasswordRepeatPlaceholder')}
                    required
                  />
                </div>
              </div>
              <button type="submit" className="login-submit-button" disabled={isLoading}>
                {isLoading ? t('login.updating') : t('login.updatePassword')}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

const LoginModal = ({ isOpen, onClose, onLogin }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  const EMAIL_DOMAIN = '@kocaelisaglik.edu.tr';

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let nextValue = value;
    if (name === 'username') {
      // Kullanıcı tam mail yapıştırırsa @kocaelisaglik.edu.tr'yi kırp; yerelde
      // sadece kullanıcı adı kısmı tutuluyor, gönderirken suffix ekleniyor.
      const atIdx = nextValue.indexOf('@');
      if (atIdx >= 0) nextValue = nextValue.slice(0, atIdx);
    }
    setFormData(prev => ({
      ...prev,
      [name]: nextValue
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const usernameTrim = (formData.username || '').trim();
    const fullEmail = usernameTrim.includes('@')
      ? usernameTrim
      : `${usernameTrim}${EMAIL_DOMAIN}`;

    const loginData = {
      email: fullEmail,
      password: formData.password
    };

    try {
      let response = await fetch(API_ENDPOINTS.AKADEMISYEN_LOGIN, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginData)
      });
      let data = await response.json();

      if (data.success) {
        sessionStorage.setItem('userRole', 'akademisyen');
        if (data.akademisyen_id) {
          sessionStorage.setItem('akademisyen_id', data.akademisyen_id);
        }
        if (data.user && data.user.program_ciktisi_yetkisi !== undefined) {
          sessionStorage.setItem('program_ciktisi_yetkisi', data.user.program_ciktisi_yetkisi);
        }
        onLogin(data.user);
        onClose();
        setFormData({ username: '', password: '' });
        setIsLoading(false);
        return;
      }

      response = await fetch(API_ENDPOINTS.ADMIN_LOGIN, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginData)
      });
      data = await response.json();

      if (data.success) {
        sessionStorage.setItem('userRole', 'admin');
        window.location.href = '/admin';
        setFormData({ username: '', password: '' });
        setIsLoading(false);
        return;
      }

      setIsLoading(false);
      alert(t('login.wrongCredentials'));
    } catch (err) {
      setIsLoading(false);
      alert(t('login.serverError'));
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="login-modal-overlay" onClick={handleOverlayClick}>
        <div className="login-modal">
          <div className="login-modal-header">
            <h2>{t('login.title')}</h2>
            <button className="close-button" onClick={onClose}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>

          <div className="login-modal-body">
            <form onSubmit={handleSubmit} className="login-form">
              <div className="form-group">
                <label htmlFor="username">{t('login.email')}</label>
                <div className="input-wrapper input-wrapper--with-suffix">
                  <input
                    type="text"
                    id="username"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    placeholder="kullanici.adi"
                    autoComplete="username"
                    required
                  />
                  <span className="email-suffix">@kocaelisaglik.edu.tr</span>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="password">{t('login.password')}</label>
                <div className="input-wrapper">
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder={t('login.passwordPlaceholder')}
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                className="login-submit-button"
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="loading-spinner">
                    <div className="spinner"></div>
                    {t('login.loggingIn')}
                  </span>
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
                      <polyline points="10,17 15,12 10,7"></polyline>
                      <line x1="15" y1="12" x2="3" y2="12"></line>
                    </svg>
                    {t('login.loginButton')}
                  </>
                )}
              </button>
            </form>

            <div className="login-footer">
              <button type="button" className="forgot-password" onClick={() => setForgotOpen(true)}>
                <svg className="forgot-password-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 16.5H9v1.5a1.5 1.5 0 01-1.5 1.5H6v1.5a1.5 1.5 0 01-1.5 1.5H3a1.5 1.5 0 01-1.5-1.5V14.25L11.308 6.462A6 6 0 0117 2a5.973 5.973 0 013.5 1.5" />
                </svg>
                {t('login.forgotPassword')}
              </button>
            </div>
          </div>
        </div>
      </div>
      <ForgotPasswordModal isOpen={forgotOpen} onClose={() => setForgotOpen(false)} />
    </>
  );
};

export default LoginModal;
